import { FC, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	useModal,
	useSubscriptionPayments,
	useUpdateSubscription
} from 'Hooks';
import {
	clx,
	currencyFormatter,
	formatLocaleDateTime,
	getRemainingDay,
	subStatus
} from 'Utils';
import {
	Badge,
	Button,
	LoadingContent,
	SwitchController,
	FontAwesome,
	ModalBoxPaymentMethodList,
	ModalBoxPaymentResult
} from 'Elements';
import {
	Colors,
	PaymentStatus,
	SubscriptionDetails,
	SubscriptionStatus
} from 'Types';
import styles from 'partials/card/subscription-card.module.scss';

interface Props {
	subscription: SubscriptionDetails;
	subscriptionId: string;
	subscriptionPayments?: any;
	isLoading?: boolean;
	canAutoRenew?: boolean;
	communityPartnerInfo?: any;
	showPrice?: boolean;
	refetchPayments?: () => void;
}

export const DetailsSubscription: FC<Props> = ({
	subscription,
	subscriptionPayments,
	subscriptionId,
	refetchPayments,
	isLoading,
	communityPartnerInfo,
	canAutoRenew = true,
	showPrice = true
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [openModalResulte, toggleModalResulte] = useModal();
	const subscriptionPaymentStatusUrl =
		parseInt(searchParams.get('subscriptionPaymentStatus') as string) ?? -1;
	const [subscriptionPaymentStatus, setSubscriptionPaymentStatus] = useState(
		subscriptionPaymentStatusUrl ?? -1
	);
	const status = subscription?.status;
	const showModal = searchParams.get('showModal') === 'true' ? true : false;
	const [openModal, toggleModal] = useModal();
	const canAutoRenewAndSwitch =
		canAutoRenew && status === SubscriptionStatus.active;
	const canSwitchAgent = canAutoRenewAndSwitch;
	const row_class = 'justify-content-between justify-content-md-start';
	const defaultImage = '/assets/img/business-partner-default.png';

	const { updateSubscription } = useUpdateSubscription(
		subscriptionId as string,
		{ enabled: !!subscriptionId && canAutoRenew }
	);

	const getStatusData = () => {
		return subStatus.find((elem) => elem.value === status);
	};

	const { reset, control } = useForm();

	const handleUpdate = async (autoRenewal: boolean) => {
		await updateSubscription(autoRenewal, undefined, () =>
			reset({
				autoRenewal: subscription?.autoRenewal ?? true
			})
		);
	};

	const handleViewSwitchedSub = () => {
		navigate(`/subscriptions/${subscription.subCompanySubscription.id}`, {
			state: { prevPath: `/subscriptions/${subscription.id}` }
		});
	};

	useEffect(() => {
		reset({
			autoRenewal: subscription?.autoRenewal
		});
	}, [reset, subscription]);

	const onClickPay = () => {
		setSearchParams({ showModal: 'true' });
	};

	useEffect(() => {
		if (
			showModal &&
			subscription?.status === SubscriptionStatus.pendingPayment
		) {
			if (subscriptionPaymentStatusUrl === PaymentStatus.InProcess) {
				setSubscriptionPaymentStatus(subscriptionPaymentStatusUrl);
				toggleModalResulte();
			} else {
				toggleModal();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscription, showModal]);

	const {
		data: subscriptionPaymentsUpgrade,
		refetch: refetchPaymentsUpgrade
	} = useSubscriptionPayments(
		subscription?.upgradeSubscriptionPaymentId ?? '',
		{
			enabled: !!subscription?.upgradeSubscriptionPaymentId
		}
	);

	return (
		<Row>
			<Col
				md={12}
				xl={
					canAutoRenewAndSwitch ||
					(subscription?.status === 3 &&
						(subscriptionPayments?.status ===
							PaymentStatus.Pending ||
							subscriptionPayments?.status ===
								PaymentStatus.Failed))
						? 9
						: 12
				}>
				<Row>
					<Col md={5}>
						<div className={clx('d-flex', styles.row, row_class)}>
							<span className={styles.rowLabel}>
								{t('label.name')}:
							</span>
							<div className="d-flex align-items-center gap-2">
								<span className="text-primary fs-6 fw-700">
									{subscription?.subscriptionPlanInfo?.name ??
										'---'}
								</span>
							</div>
						</div>
						<div className={clx(styles.row, row_class)}>
							<span className={styles.rowLabel}>
								{t('label.subscription-code')}:
							</span>
							<span>
								{subscription?.subscriptionPlanInfo
									?.subscriptionPlanCode ?? '---'}
							</span>
						</div>
						{showPrice && (
							<div className={clx(styles.row, row_class)}>
								<span className={styles.rowLabel}>
									{t('label.price')}:
								</span>
								<span className="fw-500">
									{currencyFormatter(
										subscriptionPayments?.price
											?.totalAmount ?? 0
									)}
								</span>
							</div>
						)}
						<div className={clx(styles.row, row_class)}>
							<span className={styles.rowLabel}>
								{t('label.status')}:
							</span>
							<Badge
								color={getStatusData()?.color ?? Colors.muted}>
								{t(`${getStatusData()?.label}`)}
							</Badge>
						</div>
						<div className={clx(styles.row, row_class)}>
							<span className={styles.rowLabel}>
								{t('label.subscription-type')}:
							</span>
							{subscription?.subscriptionPlanInfo?.type === 1 && (
								<div>
									<Badge
										color={Colors.premium}
										className="gap-2">
										<FontAwesome icon={'medal'} size="lg" />
										{t('button.premium')}
									</Badge>
								</div>
							)}
							{subscription?.subscriptionPlanInfo?.type !== 1 && (
								<div>
									<Badge
										color={Colors.association}
										className="gap-2">
										{t('button.standard')}
									</Badge>
								</div>
							)}
						</div>
					</Col>
					<Col md={7}>
						<div className={clx(styles.row, row_class)}>
							<span
								className={clx(
									styles.rowLabel,
									'fs-sm fw-400 color-gray-4'
								)}>
								{t('label.purchase-date')}:
							</span>
							<span>
								{subscription?.startDate
									? formatLocaleDateTime(
											subscription?.startDate
									  )
									: '----'}
							</span>
						</div>
						<div className={clx(styles.row, row_class)}>
							<span
								className={clx(
									styles.rowLabel,
									'fs-sm fw-500 color-gray-4'
								)}>
								{status === SubscriptionStatus.active &&
									(subscription?.autoRenewal
										? t('title.renew-at')
										: t('title.expire-on'))}
								{(status ===
									SubscriptionStatus.pendingApproval ||
									status ===
										SubscriptionStatus.pendingPayment) &&
									t('title.expire-on')}
								{status === SubscriptionStatus.expired &&
									t('title.expire-on')}
							</span>
							<span className="fw-500">
								{status === SubscriptionStatus.active &&
									(subscription?.renewDate
										? `${formatLocaleDateTime(
												subscription?.renewDate,
												false
										  )} (${getRemainingDay(
												new Date(),
												new Date(
													subscription?.renewDate
												)
										  )} ${t('label.days')})`
										: `${formatLocaleDateTime(
												subscription?.endDate,
												false
										  )} (${getRemainingDay(
												new Date(),
												new Date(subscription?.endDate)
										  )} ${t('label.days')})`)}
								{(status ===
									SubscriptionStatus.pendingPayment ||
									status === SubscriptionStatus.expired) &&
									formatLocaleDateTime(
										subscription?.endDate,
										false
									)}
								{/* {status === SubscriptionStatus.expired &&
										formatLocaleDateTime(
											subscription?.status.updatedAt,
											false
										)} */}
							</span>
						</div>
						<div
							className={clx(
								styles.row,
								row_class,
								canSwitchAgent && 'gap-2'
							)}>
							<span className={styles.rowLabel}>
								{t('title.community-partner')}:
							</span>
							{communityPartnerInfo && (
								<div className="d-flex align-items-center flex-grow-1 justify-content-end justify-content-md-start gap-2">
									<span className="fs-sm fw-400">
										{communityPartnerInfo.organizationName ??
											'---'}
									</span>
									{communityPartnerInfo?.commissionGroup
										?.IsPremium && (
										<Badge
											color={Colors.premium}
											className="gap-2">
											<FontAwesome
												icon={'medal'}
												size="lg"
											/>
											{
												communityPartnerInfo
													?.commissionGroup.label
											}
										</Badge>
									)}
								</div>
							)}
						</div>
						{status === SubscriptionStatus.expired &&
							subscription?.subCompanySubscription && (
								<div
									className={clx(
										styles.row,
										row_class,
										'gap-2 gap-xxl-4'
									)}>
									<div className="d-flex align-items-center">
										<span className={styles.rowLabel}>
											{t('title.switched-agent')}:
										</span>
										<div className="d-flex align-items-center justify-content-end justify-content-md-start flex-grow-1 flex-wrap gap-2">
											<img
												src={
													subscription
														.subCompanySubscription
														.communityPartnerInfo
														.thumbnailLogo ||
													defaultImage
												}
												alt={
													subscription
														.subCompanySubscription
														.communityPartnerInfo
														.organizationName ??
													'---'
												}
												className={clx(
													styles.logo,
													styles.logoSm
												)}
											/>
											<span className="fs-sm fw-400">
												{subscription
													.subCompanySubscription
													.communityPartnerInfo
													.organizationName ?? '---'}
											</span>
										</div>
									</div>
									<Button
										size="sm"
										color={Colors['white-primary']}
										onClick={handleViewSwitchedSub}>
										<FontAwesome
											icon="eye"
											size="lg"
											className="me-2 flex-shrink-0"
										/>
										{t('button.view-sub')}
									</Button>
								</div>
							)}
					</Col>
				</Row>
			</Col>
			<Col md={12} xl={3} className="mt-4 mt-md-4 mt-xl-0">
				{canAutoRenewAndSwitch && (
					<div className="mb-4">
						<div className={clx(styles.row, 'border-0 gap-2')}>
							<span className={styles.rowLabel}>
								{t('forms.auto-renewal')}:
							</span>
							<SwitchController
								control={control}
								name="autoRenewal"
								label={
									subscription?.autoRenewal
										? 'label.active'
										: 'label.inactive'
								}
								onSwitchChange={handleUpdate}
							/>
						</div>
						<p className="mt-2 mb-0 text-gray-3 lh-md fs-small">
							{t('text.auto-renewal-terms')}
						</p>
					</div>
				)}
				{subscription?.status === 3 &&
					(subscriptionPayments?.status === PaymentStatus.Pending ||
						subscriptionPayments?.status ===
							PaymentStatus.Failed) && (
						<div className="d-flex justify-content-end">
							<div className="d-flex flex-column gap-2">
								<span>
									{t(
										'text.you-can-pay-your-subscription-invoice'
									)}
								</span>
								<div className="d-flex justify-content-end">
									<Button
										onClick={() => {
											onClickPay();
										}}
										size="sm"
										className="px-3 text-white bg-primary"
										label={t('button.pay') as string}
									/>
								</div>
							</div>
						</div>
					)}
			</Col>
			{isLoading && <LoadingContent />}
			<ModalBoxPaymentMethodList
				isVoucher={true}
				selectedPlanId={
					subscriptionPayments?.subscriptionPlanInfo
						?.subscriptionPlanId
				}
				communityPartnerId={
					subscription?.communityPartnerInfo?.communityPartnerId
				}
				voucherCodeCurrent={
					subscriptionPaymentsUpgrade?.price?.voucherCode
						? subscriptionPaymentsUpgrade?.price?.voucherCode
						: subscriptionPayments?.price?.voucherCode
				}
				selectedPlanType={
					subscriptionPayments?.subscriptionPlanInfo?.type
				}
				subscriptionId={subscriptionId}
				urlBack={`/subscriptions/${subscriptionId}?`}
				returnUrl={`subscriptions/${subscriptionId}?showModal=true&`}
				totalAmount={
					subscriptionPaymentsUpgrade?.price?.totalAmount
						? subscriptionPaymentsUpgrade?.price?.totalAmount
						: subscriptionPayments?.price?.totalAmount
						? subscriptionPayments?.price?.totalAmount
						: 0
				}
				subscriptionDuration={
					subscriptionPaymentsUpgrade?.price?.subscriptionDuration
						? subscriptionPaymentsUpgrade?.price
								?.subscriptionDuration
						: subscriptionPayments?.price?.subscriptionDuration
				}
				currentSubscriptionPaymentId={
					subscription?.upgradeSubscriptionPaymentId
						? subscription?.upgradeSubscriptionPaymentId
						: subscription?.currentSubscriptionPaymentId
				}
				refetch={() => {
					refetchPayments && refetchPayments();
					refetchPaymentsUpgrade();
				}}
				isOpen={openModal}
				toggleModalResulte={toggleModalResulte}
				onClose={toggleModal}
				subscriptionPaymentStatus={subscriptionPaymentStatus}
				setSubscriptionPaymentStatus={setSubscriptionPaymentStatus}
			/>
			<ModalBoxPaymentResult
				subscriptionId={subscription?.id}
				subscriptionCalculate={{
					totalAmount: subscriptionPayments?.price?.totalAmount
						? subscriptionPayments?.price?.totalAmount
						: 0,
					subscriptionDuration:
						subscriptionPayments?.price?.subscriptionDuration
				}}
				subscriptionPaymentStatus={subscriptionPaymentStatus}
				isOpen={openModalResulte}
				onClose={toggleModalResulte}
			/>
		</Row>
	);
};
